import { COST_BREAKDOWN_DECIMAL_PRECISION } from "../constants"
import type { CostBreakdownAmountPercentageValues, CostBreakdownStandardField } from "../types"
import { AmountPercentageCalculationType } from "../types"

import { roundToPrecision } from "@/utils/number"

export const calculatePercentageFromAmount = (
  amount: number | null | undefined,
  total: number | null | undefined
): number | null => {
  // eslint-disable-next-line eqeqeq
  if (amount != null && total != null && total > 0) {
    return roundToPrecision((amount * 100) / total, COST_BREAKDOWN_DECIMAL_PRECISION)
  }
  return null
}

export const recalculatePercentageForTotalBareCostChange = (
  amount: number | null | undefined,
  totalBareCost: number | null | undefined
): number | null => {
  return calculatePercentageFromAmount(amount, totalBareCost)
}

export const calculateAmountFromPercentage = (
  percentage: number | null | undefined,
  total: number | null | undefined
): number | null => {
  // eslint-disable-next-line eqeqeq
  if (percentage != null && total != null && total > 0) {
    const rawCalculation = (total * percentage) / 100
    return roundToPrecision(rawCalculation, COST_BREAKDOWN_DECIMAL_PRECISION)
  }
  return null
}

export const hasAmountChanged = (currentAmount: number | null, lastAmount: number | null): boolean => {
  return currentAmount !== lastAmount
}

export const hasPercentageChanged = (currentPercentage: number | null, lastPercentage: number | null): boolean => {
  return currentPercentage !== lastPercentage
}

export interface AmountPercentageCalculationResult {
  type: AmountPercentageCalculationType
  newAmount?: number | null
  newPercentage?: number | null
  shouldClear?: boolean
}

export const calculateAmountPercentagePairUpdate = (
  currentAmount: number | null,
  currentPercentage: number | null,
  lastAmount: number | null,
  lastPercentage: number | null,
  totalBareCost: number | null
): AmountPercentageCalculationResult => {
  // Case 1: Amount changed - calculate percentage or clear if amount is empty
  if (hasAmountChanged(currentAmount, lastAmount)) {
    if (currentAmount && totalBareCost) {
      const newPercentage = calculatePercentageFromAmount(currentAmount, totalBareCost)
      return {
        type: AmountPercentageCalculationType.AmountChanged,
        newAmount: currentAmount,
        newPercentage,
      }
    } else if (!currentAmount) {
      return {
        type: AmountPercentageCalculationType.AmountChanged,
        newAmount: null,
        newPercentage: null,
        shouldClear: true,
      }
    }
  }
  // Case 2: Percentage changed - calculate amount or clear if percentage is empty
  else if (hasPercentageChanged(currentPercentage, lastPercentage)) {
    if (currentPercentage && totalBareCost) {
      const newAmount = calculateAmountFromPercentage(currentPercentage, totalBareCost)
      return {
        type: AmountPercentageCalculationType.PercentageChanged,
        newAmount,
        newPercentage: currentPercentage,
      }
    } else if (!currentPercentage) {
      return {
        type: AmountPercentageCalculationType.PercentageChanged,
        newAmount: null,
        newPercentage: null,
        shouldClear: true,
      }
    }
  }

  return { type: AmountPercentageCalculationType.NoChange }
}

export const updateLastValues = (
  amount: number | null,
  percentage: number | null
): CostBreakdownAmountPercentageValues => ({
  amount,
  percentage,
})

export interface BatchAmountPercentageUpdate {
  fieldName: string | CostBreakdownStandardField
  amountPath: string
  percentagePath: string
  update: AmountPercentageCalculationResult
}

export const batchCalculateAmountPercentagePairs = (
  pairs: {
    fieldName: string | CostBreakdownStandardField
    amountPath: string
    percentagePath: string
    currentAmount: number | null
    currentPercentage: number | null
    lastAmount: number | null
    lastPercentage: number | null
  }[],
  totalBareCost: number | null
): BatchAmountPercentageUpdate[] => {
  return pairs.map((pair) => ({
    fieldName: pair.fieldName,
    amountPath: pair.amountPath,
    percentagePath: pair.percentagePath,
    update: calculateAmountPercentagePairUpdate(
      pair.currentAmount,
      pair.currentPercentage,
      pair.lastAmount,
      pair.lastPercentage,
      totalBareCost
    ),
  }))
}
